import { useState } from "react";
import type { CarbonProject } from "@/mocks/carbono";
import CarbonStatusBadge from "./CarbonStatusBadge";
import CarbonConfidenceBadge from "./CarbonConfidenceBadge";

interface CarbonProjectCardProps {
  project: CarbonProject;
  onOpen: (project: CarbonProject) => void;
}

export default function CarbonProjectCard({ project, onOpen }: CarbonProjectCardProps) {
  const [showActions, setShowActions] = useState(false);

  const typeIcons: Record<string, string> = {
    "Carbono Urbano": "ri-leaf-line",
    "Arborização Urbana": "ri-tree-line",
    "Compensação Ambiental": "ri-exchange-line",
    "Replantio": "ri-seedling-line",
    "Recuperação de Área Degradada": "ri-recycle-line",
    "UC / Área Protegida": "ri-shield-line",
    "Corredor Verde": "ri-road-map-line",
    "ESG Corporativo": "ri-building-line",
  };

  return (
    <div
      className="bg-white rounded-xl border border-background-200/70 p-5 hover:-translate-y-1 hover:border-primary-300/70 transition-all duration-300 cursor-pointer"
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      onClick={() => onOpen(project)}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center">
            <i className={`${typeIcons[project.type] || "ri-leaf-line"} text-primary-600 w-full h-full flex items-center justify-center text-lg`} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground-900 leading-tight">{project.name}</h3>
            <div className="text-[11px] text-foreground-500 mt-0.5">{project.type}</div>
          </div>
        </div>
        <CarbonStatusBadge status={project.status} />
      </div>

      <div className="grid grid-cols-2 gap-y-2 gap-x-3 mb-3">
        <div>
          <div className="text-[11px] text-foreground-400">Cliente</div>
          <div className="text-xs font-medium text-foreground-700 truncate">{project.client}</div>
        </div>
        <div>
          <div className="text-[11px] text-foreground-400">Município</div>
          <div className="text-xs font-medium text-foreground-700">{project.municipality}/{project.uf}</div>
        </div>
        <div>
          <div className="text-[11px] text-foreground-400">Área</div>
          <div className="text-xs font-medium text-foreground-700">{project.areaHa} ha</div>
        </div>
        <div>
          <div className="text-[11px] text-foreground-400">Árvores</div>
          <div className="text-xs font-medium text-foreground-700">{project.treesCount.toLocaleString("pt-BR")}</div>
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-background-100 pt-3">
        <div className="flex items-center gap-2">
          <div className="text-xs font-semibold text-primary-700">{project.co2eEstimated.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} t CO2e</div>
          <CarbonConfidenceBadge level={project.confidenceLevel >= 80 ? "alta" : project.confidenceLevel >= 60 ? "média" : "baixa"} />
        </div>
        <div className="text-[11px] text-foreground-400">Atualizado {project.lastUpdate}</div>
      </div>

      {showActions && (
        <div className="flex gap-2 mt-3 pt-3 border-t border-background-100 animate-in fade-in slide-in-from-bottom-2 duration-200">
          <button
            onClick={(e) => { e.stopPropagation(); onOpen(project); }}
            className="flex-1 px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 rounded-md hover:bg-primary-100 transition-colors whitespace-nowrap"
          >
            Abrir
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); }}
            className="flex-1 px-3 py-1.5 text-xs font-medium text-accent-700 bg-accent-50 rounded-md hover:bg-accent-100 transition-colors whitespace-nowrap"
          >
            Mapa
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); }}
            className="flex-1 px-3 py-1.5 text-xs font-medium text-foreground-600 bg-background-50 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap"
          >
            Relatório
          </button>
        </div>
      )}
    </div>
  );
}